import { useState } from "react";
import axios from "axios";
import { Shield, CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatQty = (v) => (v == null ? "\u2014" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

/**
 * Admin-only adjustment panel on a Store Request (Oct 1 2026, user's ask).
 * Two cases that used to need a direct DB edit:
 *   A. SAP failed but the request closed as issued -> reopen for re-issue.
 *   B. SAP posted but no GM/GAC id reached the app -> record the verified id.
 * A remark is mandatory for both and is kept on the request's adjustment trail.
 */
export const AdminAdjustmentPanel = ({ request, onDone }) => {
  const [open, setOpen] = useState(false);
  const [remark, setRemark] = useState("");
  const [externalId, setExternalId] = useState("");
  const [busy, setBusy] = useState(false);
  const components = request.components || [];
  const unposted = components.filter((c) => !c.goods_movement?.ok);
  const [picked, setPicked] = useState(() => new Set(unposted.map((c) => c.product_id)));
  const adjustments = request.admin_adjustments || [];
  const isOpenForIssue = ["pending", "issuing", "resolved_balance_pending"].includes(request.status);

  const toggle = (productId) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(productId)) next.delete(productId);
    else next.add(productId);
    return next;
  });

  const run = async (action) => {
    if (!remark.trim()) { toast.error("A remark is required"); return; }
    if (action === "record" && !externalId.trim()) { toast.error("Enter the SAP movement/confirmation ID"); return; }
    if (action === "record" && picked.size === 0) { toast.error("Select at least one component"); return; }
    setBusy(true);
    try {
      if (action === "reopen") {
        await axios.post(`${API}/store-requests/${request._id}/admin-reopen`, { remark: remark.trim() });
        toast.success("Reopened for re-issue - unposted quantities cleared");
      } else {
        await axios.post(`${API}/store-requests/${request._id}/admin-record-movement`, {
          external_id: externalId.trim(), remark: remark.trim(), product_ids: [...picked],
        });
        toast.success(`SAP movement ${externalId.trim()} recorded`);
      }
      setRemark("");
      setExternalId("");
      await onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Adjustment failed", { duration: 15000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#D0D5DD] rounded-sm" data-testid="store-admin-adjustment-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-[#344054] uppercase tracking-wide"
        data-testid="store-admin-adjustment-toggle"
      >
        <span className="flex items-center gap-1.5"><Shield size={14} className="text-[#0E7C86]" /> Admin Adjustment</span>
        <span className="text-[10px] font-normal text-[#667085] normal-case">
          {adjustments.length > 0 ? `${adjustments.length} adjustment(s) on record` : "SAP failed but closed as issued, or posted without a GAC ID"}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#EAECF0] p-3 space-y-4">
          <div className="space-y-1">
            <Label className="text-xs font-bold text-[#344054]">Remark (required)</Label>
            <Textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="e.g. Verified in SAP: no movement exists for this request, reopening for the store to re-issue"
              className="text-xs"
              data-testid="store-admin-adjustment-remark"
            />
          </div>

          <div className="rounded-sm border border-[#EAECF0] p-3 space-y-2">
            <p className="text-xs font-bold text-[#344054] flex items-center gap-1.5">
              <ArrowCounterClockwise size={13} /> SAP failed, but this request closed as issued
            </p>
            <p className="text-[11px] text-[#667085]">
              Clears every quantity SAP did not accept and puts the request back in Balance Pending so the store can issue it again.
              Quantities with a real SAP movement are left untouched.
            </p>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || isOpenForIssue}
              onClick={() => run("reopen")}
              data-testid="store-admin-reopen-btn"
            >
              {isOpenForIssue ? `Already open (${request.status})` : "Reopen for re-issue"}
            </Button>
          </div>

          <div className="rounded-sm border border-[#EAECF0] p-3 space-y-2">
            <p className="text-xs font-bold text-[#344054] flex items-center gap-1.5">
              <CheckCircle size={13} /> SAP posted, but no GAC / movement ID is visible here
            </p>
            <p className="text-[11px] text-[#667085]">
              Records the ID you verified in SAP against the components below. No SAP call is made, so nothing can move twice.
            </p>
            <Input
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder="SAP Goods Movement / Confirmation ID"
              className="h-8 text-xs font-mono w-72"
              data-testid="store-admin-external-id"
            />
            {unposted.length === 0 ? (
              <p className="text-[11px] text-[#027A48]">Every component already carries a SAP movement ID.</p>
            ) : (
              <div className="space-y-1">
                {unposted.map((c, i) => (
                  <label key={c.product_id} className="flex items-center gap-2 text-[11px] text-[#344054]" data-testid={`store-admin-component-${i}`}>
                    <input
                      type="checkbox"
                      checked={picked.has(c.product_id)}
                      onChange={() => toggle(c.product_id)}
                      data-testid={`store-admin-component-check-${i}`}
                    />
                    <span className="font-mono">{c.product_id}</span>
                    <span className="text-[#667085]">
                      {formatQty(c.issued_this_round || c.rejected_qty_last_round || c.issued_qty)} {c.unit_of_measure}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={busy || unposted.length === 0}
              onClick={() => run("record")}
              data-testid="store-admin-record-movement-btn"
            >
              Record SAP movement ID
            </Button>
          </div>

          {adjustments.length > 0 && (
            <div className="space-y-1" data-testid="store-admin-adjustment-trail">
              <p className="text-xs font-bold text-[#344054]">Adjustment trail</p>
              {adjustments.map((a, i) => (
                <p key={i} className="text-[11px] text-[#667085]" data-testid={`store-admin-adjustment-${i}`}>
                  <span className="font-mono text-[#101828]">{String(a.at).replace("T", " ").slice(0, 16)}</span>{" "}
                  <span className="font-bold">{a.action === "reopened_for_reissue" ? "Reopened for re-issue" : `Recorded SAP movement ${a.details?.external_id || ""}`}</span>{" "}
                  by {a.actor} &mdash; {a.remark}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
